import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leaveService, userService } from '../services/api.service';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AppLayout from '../layouts/AppLayout';

const LeaveRequestReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [leaveRequest, setLeaveRequest] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [employeeEmail, setEmployeeEmail] = useState('');
  
  useEffect(() => {
    const fetchLeaveRequest = async () => {
      try {
        setLoading(true);
        // We need to get all leave requests and find the specific one
        const response = await leaveService.getAllLeaveRequests();
        const request = response.leaveRequests.find(req => req.id === parseInt(id));
        
        if (!request) {
          setError('Leave request not found');
        } else if (request.status !== 'pending') {
          setError('This leave request has already been processed');
        } else {
          setLeaveRequest(request);
          
          // Fetch employee email
          try {
            const userResponse = await userService.getUserById(request.user_id);
            setEmployeeEmail(userResponse.user.email);
          } catch (userErr) {
            console.error('Error fetching user email:', userErr);
            // Continue anyway, will just not send email confirmation
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching leave request:', err);
        setError('Failed to load leave request details');
        setLoading(false);
      }
    };
    
    fetchLeaveRequest();
  }, [id]);
  
  const handleApprove = async () => {
    try {
      setSubmitting(true);
      
      console.log('Approving leave request with comment:', comment);
      
      // Update leave request status - the backend will handle email notifications
      const updatedRequest = await leaveService.updateLeaveRequestStatus(id, {
        status: 'approved',
        comment: comment.trim() || undefined // Send comment if provided
      });
      
      console.log('Leave request approved:', updatedRequest);
      
      navigate('/leave-requests', {
        state: { success: 'Leave request approved successfully' }
      });
    } catch (err) {
      console.error('Error approving leave request:', err);
      setError(err.response?.data?.message || 'Failed to approve leave request');
      setSubmitting(false);
    }
  };
  
  const handleReject = async () => {
    if (!comment.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    
    try {
      setSubmitting(true);
      
      console.log('Rejecting leave request with comment:', comment);
      
      // Update leave request status - the backend will handle email notifications
      const updatedRequest = await leaveService.updateLeaveRequestStatus(id, {
        status: 'rejected',
        comment: comment.trim()
      });
      
      console.log('Leave request rejected:', updatedRequest);
      
      navigate('/leave-requests', {
        state: { success: 'Leave request rejected successfully' }
      });
    } catch (err) {
      console.error('Error rejecting leave request:', err);
      setError(err.response?.data?.message || 'Failed to reject leave request');
      setSubmitting(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Check if request is half-day
  const isHalfDayRequest = (request) => {
    // Check multiple conditions to determine if it's a half-day
    const hasHalfDayType = request.request_type && request.request_type !== 'full-day';
    const hasHalfDayDuration = request.working_days && parseFloat(request.working_days) === 0.5;
    const isSameDate = request.start_date === request.end_date;
    
    return hasHalfDayType || hasHalfDayDuration || (isSameDate && hasHalfDayDuration);
  };
  
  // FIXED: Better duration display for half-days
  const formatDuration = (request) => {
    const isHalfDay = isHalfDayRequest(request);
    
    // Use working_days if available (supports decimal)
    if (request.working_days !== undefined && request.working_days !== null) {
      const days = parseFloat(request.working_days);
      
      if (isHalfDay || days === 0.5) {
        const halfDayType = request.request_type ? 
          request.request_type.replace('-', ' ') : 'half-day';
        return `0.5 days (${halfDayType})`;
      }
      
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }
    
    // For half-day requests without working_days field
    if (isHalfDay) {
      const halfDayType = request.request_type ? 
        request.request_type.replace('-', ' ') : 'half-day';
      return `0.5 days (${halfDayType})`;
    }
    
    // Fallback calculation for older full-day requests
    const calculateDuration = (startDate, endDate) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    };
    
    return `${calculateDuration(request.start_date, request.end_date)} days`;
  };
  
  // UPDATED: Better date range display for half-days
  const formatDateRange = (request) => {
    const startFormatted = formatDate(request.start_date);
    const endFormatted = formatDate(request.end_date);
    const isHalfDay = isHalfDayRequest(request);
    
    // If it's the same date (half-day), show it differently
    if (request.start_date === request.end_date || isHalfDay) {
      const halfDayType = request.request_type ? 
        ` (${request.request_type.replace('-', ' ')})` : '';
      return `${startFormatted}${halfDayType}`;
    }
    
    return `${startFormatted} - ${endFormatted}`;
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="text-center py-10">Loading leave request...</div>
      </AppLayout>
    );
  }
  
  if (error) {
    return (
      <AppLayout>
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-6">
            <div className="bg-red-50 text-red-500 p-4 rounded-md">
              {error}
            </div>
            <div className="mt-4 text-center">
              <Button onClick={() => navigate('/leave-requests')}>
                Back to Leave Requests
              </Button>
            </div>
          </CardContent>
        </Card>
      </AppLayout>
    );
  }
  
  if (!leaveRequest) {
    return (
      <AppLayout>
        <div className="text-center py-10">Leave request not found</div>
      </AppLayout>
    );
  }
  
  const isHalfDay = isHalfDayRequest(leaveRequest);
  
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader className={isHalfDay ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200" : ""}>
            <CardTitle className="text-xl flex items-center">
              Review Leave Request
              {isHalfDay && (
                <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                  🕐 Half-day Request
                </span>
              )}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Employee Information */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-2">Employee Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{leaveRequest.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Department</p>
                  <p className="font-medium">{leaveRequest.department}</p>
                </div>
                {employeeEmail && (
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{employeeEmail}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Leave Details */}
            <div className={`p-4 rounded-lg ${isHalfDay ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
              <h3 className="font-medium mb-2 flex items-center">
                Leave Details
                {isHalfDay && (
                  <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded">
                    HALF-DAY
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Leave Type</p>
                  <p className="font-medium">{leaveRequest.leave_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                </div>
                
                {/* Show request type for half-days */}
                {isHalfDay && leaveRequest.request_type && (
                  <div>
                    <p className="text-sm text-gray-500">Request Type</p>
                    <div className="flex items-center">
                      <span className="font-medium capitalize text-blue-700">
                        {leaveRequest.request_type.replace('-', ' ')}
                      </span>
                      <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                        {leaveRequest.request_type === 'morning-half' ? '🌅 AM' : '🌆 PM'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div>
                  <p className="text-sm text-gray-500">Date Range</p>
                  <p className={`font-medium ${isHalfDay ? 'text-blue-700' : ''}`}>
                    {formatDateRange(leaveRequest)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <div className="flex items-center">
                    <p className={`font-medium ${isHalfDay ? 'text-blue-700' : ''}`}>
                      {formatDuration(leaveRequest)}
                    </p>
                    {isHalfDay && (
                      <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">
                        -0.5 days from balance
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Reason</p>
                  <p className="font-medium">
                    {leaveRequest.reason || 'No reason provided'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Enhanced Half-day explanation */}
            {isHalfDay && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-400 rounded-lg">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">ℹ️</span>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Half-day Request Summary</h4>
                    <div className="space-y-1 text-sm text-blue-700">
                      <p>
                        <strong>Type:</strong> {leaveRequest.request_type ? 
                          leaveRequest.request_type.replace('-', ' ').charAt(0).toUpperCase() + 
                          leaveRequest.request_type.replace('-', ' ').slice(1) : 'Half-day'}
                      </p>
                      <p>
                        <strong>Duration:</strong> 0.5 working days
                      </p>
                      <p>
                        <strong>Impact:</strong> Will deduct 0.5 days from employee's leave balance
                      </p>
                      <p>
                        <strong>Date:</strong> {formatDate(leaveRequest.start_date)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Comment section - FIXED: Changed to textarea */}
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="comment">
                Comment (Optional for approval, required for rejection)
              </label>
              <textarea
                id="comment"
                placeholder="Add a comment or reason for approval/rejection"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500">
                This comment will be included in the email notification sent to the employee.
              </p>
            </div>
          </CardContent>
          
          <CardFooter className={`flex justify-between ${isHalfDay ? 'bg-blue-50 border-t border-blue-200' : ''}`}>
            <Button
              variant="outline"
              onClick={() => navigate('/leave-requests')}
              disabled={submitting}
            >
              Back
            </Button>
            <div className="space-x-2">
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Reject'}
              </Button>
              <Button
                onClick={handleApprove}
                disabled={submitting}
                className={isHalfDay ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                {submitting ? 'Processing...' : `Approve ${isHalfDay ? 'Half-day' : ''}`}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </AppLayout>
  );
};

export default LeaveRequestReviewPage;